import { useCallback, useMemo } from 'react';
import { Edge, Node } from '@xyflow/react';

import { ParameterManager } from '../nodes/parameters/ParameterManager';
import * as nodeValidation from '../services/node-validation';
import { NodeValidationResult, WorkflowValidationResult } from '../types/node-validation';

import { useWorkflow } from './useWorkflow';

/**
 * Hook for validating workflow nodes.
 * Uses pure validation functions - no caching, validation is computed on-demand.
 */
export function useNodeValidation() {
  const { getNodeConfig } = useWorkflow();
  const parameterManager = useMemo(() => new ParameterManager(), []);

  /**
   * Validate a single node's parameters.
   * Pure function - safe to call in render/useMemo contexts.
   */
  const validateNode = useCallback(
    (node: Node): NodeValidationResult => {
      return nodeValidation.validateNode(node, getNodeConfig, parameterManager);
    },
    [getNodeConfig, parameterManager]
  );

  /**
   * Validate both node parameters and connectivity.
   * Pure function - safe to call in render/useMemo contexts.
   */
  const validateNodesWithConnectivity = useCallback(
    (nodes: Node[], edges: Edge[]): NodeValidationResult => {
      return nodeValidation.validateNodesWithConnectivity(
        nodes,
        edges,
        getNodeConfig,
        parameterManager
      );
    },
    [getNodeConfig, parameterManager]
  );

  /**
   * Complete workflow validation including workflow-level and node-level errors.
   * Pure function - safe to call in render/useMemo contexts.
   */
  const validateWorkflow = useCallback(
    (nodes: Node[], edges: Edge[]): WorkflowValidationResult => {
      return nodeValidation.validateWorkflow(nodes, edges, getNodeConfig, parameterManager);
    },
    [getNodeConfig, parameterManager]
  );

  return {
    validateNode,
    validateNodesWithConnectivity,
    validateWorkflow,
  };
}
